import { memo } from "react";
import PropTypes from "prop-types";
import Skeleton from "@mui/material/Skeleton";

// Fixed number of skeleton cards shown while `loading` is true — mirrors
// MIN_SKELETON_ROWS in LoadingSkeletonOverlay (the desktop equivalent).
const MIN_SKELETON_CARDS = 3;

// Varied bar widths so the skeleton fields look organic rather than uniform.
const SKELETON_FIELD_WIDTHS = ["70%", "55%", "80%", "45%"];

// Single placeholder card — mirrors the shape of ResponsiveGrid's
// ResponsiveCard: header icon + title, a few body fields.
const SkeletonCard = memo(function SkeletonCard() {
  return (
    <div className="rg-card rg-card-skeleton" aria-hidden="true">
      <div className="rg-card__header">
        <div className="rg-card__icon-wrap">
          <Skeleton variant="circular" width={22} height={22} />
        </div>
        <div className="rg-card__header-text">
          <Skeleton variant="text" width={70} height={12} />
          <Skeleton variant="text" width={160} height={18} />
        </div>
      </div>
      <div className="rg-card__body">
        {SKELETON_FIELD_WIDTHS.map((w, i) => (
          <div className="rg-card__field" key={i}>
            <div className="rg-card__field-text">
              <Skeleton variant="text" width={50} height={11} />
              <Skeleton variant="text" width={w} height={14} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

SkeletonCard.displayName = "CardSkeletonOverlayCard";

/**
 * CardSkeletonOverlay
 * --------------------------------------------------------------------------
 * Mobile equivalent of LoadingSkeletonOverlay: a fixed-size stack of
 * placeholder cards, shown by ResponsiveGrid (card mode) while `loading` is
 * true, so a community/filter change reads as "loading" instead of a flash
 * of stale rows or the empty state.
 */
const CardSkeletonOverlay = ({ count = MIN_SKELETON_CARDS }) => (
  <div className="rg-card-stack" data-testid="rg-card-skeleton-overlay">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

CardSkeletonOverlay.propTypes = {
  // Number of placeholder cards to render (default MIN_SKELETON_CARDS).
  count: PropTypes.number,
};

export default CardSkeletonOverlay;
